<template>
  <div>
    <!-- Sidebar para agregar influencers -->
    <div 
      class="add-influencer-sidebar font-montserrat"
      :class="{ 'open': isVisible }"
    >
      <div class="sidebar-header">
        <div class="sidebar-title">Añadir influencers</div>
        <button class="close-button" @click="close">
          <span>&times;</span>
        </button>
      </div>
      
      <div class="sidebar-content">
        <!-- Buscador de influencers -->
        <div class="search-container mb-4">
          <div class="search-input-container">
            <feather-icon icon="SearchIcon" size="16" class="search-icon"></feather-icon>
            <input 
              type="text" 
              class="search-input font-montserrat" 
              placeholder="Buscar influencers por nombre o red social"
              v-model="searchQuery"
              @input="debounceSearch"
            >
          </div>
          
          <!-- Filtros de redes sociales como iconos -->
          <div class="social-networks-filter">
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === '' }"
              @click="selectNetwork('')"
            >
              <feather-icon icon="GridIcon" size="18"></feather-icon>
              <span class="network-name">Todas</span>
            </div>
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === 'instagram' }"
              @click="selectNetwork('instagram')"
            >
              <feather-icon icon="InstagramIcon" size="18"></feather-icon>
              <span class="network-name">Instagram</span>
            </div>
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === 'tiktok' }"
              @click="selectNetwork('tiktok')"
            >
              <feather-icon icon="VideoIcon" size="18"></feather-icon>
              <span class="network-name">TikTok</span>
            </div>
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === 'youtube' }"
              @click="selectNetwork('youtube')"
            >
              <feather-icon icon="YoutubeIcon" size="18"></feather-icon>
              <span class="network-name">YouTube</span>
            </div>
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === 'twitter' }"
              @click="selectNetwork('twitter')"
            >
              <feather-icon icon="TwitterIcon" size="18"></feather-icon>
              <span class="network-name">Twitter</span>
            </div>
            <div 
              class="network-icon-btn" 
              :class="{ 'active': filters.network === 'twitch' }"
              @click="selectNetwork('twitch')"
            >
              <feather-icon icon="TvIcon" size="18"></feather-icon>
              <span class="network-name">Twitch</span>
            </div>
          </div>
        </div>
        
        <!-- Resultados de búsqueda -->
        <div class="search-results">
          <h4 class="mb-3">Resultados</h4>
          
          <div v-if="loading" class="loading-results">
            <div class="spinner-container">
              <div class="spinner"></div>
            </div>
            <p>Buscando influencers...</p>
          </div>
          
          <div v-else-if="influencers.length === 0" class="empty-results">
            <feather-icon icon="UsersIcon" size="48" class="empty-icon mb-2"></feather-icon>
            <p>No se encontraron influencers que coincidan con tu búsqueda</p>
          </div>
          
          <div v-else class="influencer-list">
            <div 
              v-for="influencer in influencers" 
              :key="influencer.uuid || influencer.id" 
              class="influencer-item"
              :class="{ 'selected': selectedInfluencers.includes(influencer.uuid || influencer.id) }"
              @click="toggleInfluencerSelection(influencer)"
            >
              <div class="influencer-avatar">
                <img :src="influencer.image || influencer.avatar_url || defaultAvatar" :alt="influencer.username">
                <div class="network-icon-container">
                  <span class="network-icon" :class="'network-' + (influencer.network || influencer.type)">
                    <feather-icon 
                      :icon="getNetworkIcon(influencer.network || influencer.type)" 
                      size="12"
                      class="network-icon-svg"
                    ></feather-icon>
                  </span>
                </div>
              </div>
              <div class="influencer-info">
                <h5 class="influencer-name">{{ influencer.username }}</h5>
                <p class="influencer-stats">{{ formatFollowers(influencer.followers || influencer.subscribers_count) }} seguidores</p>
              </div>
              <div class="selection-checkbox">
                <feather-icon 
                  :icon="selectedInfluencers.includes(influencer.uuid || influencer.id) ? 'CheckCircleIcon' : 'CircleIcon'" 
                  size="20"
                  :class="selectedInfluencers.includes(influencer.uuid || influencer.id) ? 'text-white' : 'text-muted'"
                ></feather-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="sidebar-footer">
        <button 
          class="cancel-button" 
          @click="close"
        >
          Cancelar
        </button>
        <button 
          class="add-button" 
          :disabled="selectedInfluencers.length === 0"
          @click="addSelectedInfluencers"
        >
          Agregar {{ selectedInfluencers.length }} influencer{{ selectedInfluencers.length !== 1 ? 's' : '' }}
        </button>
      </div>
    </div>
    
    <!-- Overlay para cuando el sidebar está abierto -->
    <div 
      class="sidebar-overlay" 
      :class="{ 'visible': isVisible }"
      @click="close"
    ></div>
  </div>
</template>

<script>
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { trimText } from '@/libs/utils/formats'
import listsService from '@/services/lists'

export default {
  name: 'AddInfluencerSidebar',
  components: {
    'feather-icon': FeatherIcon
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    listId: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      loading: false,
      influencers: [],
      selectedInfluencers: [],
      filters: {
        network: ''
      },
      defaultAvatar: require('@/assets/images/avatars/usuario.png')
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetAndSearch();
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    
    resetAndSearch() {
      this.searchQuery = '';
      this.selectedInfluencers = [];
      this.filters = {
        network: ''
      };
      this.searchInfluencers();
    },
    
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.searchInfluencers();
      }, 500);
    },
    
    async searchInfluencers() {
      this.loading = true;
      
      try {
        let results = [];
        
        // Si hay consulta de búsqueda o se ha seleccionado una red social, usar el servicio
        if (this.searchQuery.trim() !== '' || this.filters.network !== '') {
          const response = await listsService.findInfluencers(
            trimText(this.searchQuery), 
            this.filters.network
          );
          
          if (response && response.list) {
            results = response.list.map(inf => ({
              id: inf.user_id || inf.channel_id,
              uuid: inf.user_id || inf.channel_id,
              username: inf.username,
              followers: inf.subscribers_count,
              network: inf.type,
              image: inf.avatar_url,
              avatar_url: inf.avatar_url,
              is_verified: inf.is_verified
            }));
          }
        } else if (!this.searchQuery && !this.filters.network) {
          // Si no hay consulta ni filtro, mostrar lista vacía o un mensaje
          results = [];
        }
        
        this.influencers = results;
      } catch (error) {
        console.error('Error al buscar influencers:', error);
        this.influencers = [];
      } finally {
        this.loading = false;
      }
    },
    
    toggleInfluencerSelection(influencer) {
      const id = influencer.uuid || influencer.id;
      const index = this.selectedInfluencers.indexOf(id);
      if (index === -1) {
        this.selectedInfluencers.push(id);
      } else {
        this.selectedInfluencers.splice(index, 1);
      }
    },
    
    async addSelectedInfluencers() {
      if (this.selectedInfluencers.length === 0) return;
      
      // Obtener los datos completos de los influencers seleccionados
      const selectedInfluencerData = this.influencers.filter(
        inf => this.selectedInfluencers.includes(inf.uuid || inf.id)
      );
      
      try {
        this.loading = true;
        
        // Preparar los datos para la API
        const formData = {
          influencers: selectedInfluencerData.map(inf => ({
            influencer: inf.username,
            username: inf.username,
            network: inf.network || inf.type,
            image: inf.image || inf.avatar_url,
            followers: inf.followers || inf.subscribers_count,
            is_verified: inf.is_verified
          }))
        };
        
        // Llamar al servicio para agregar los influencers en bloque
        await listsService.bulkAddInfluencer(this.listId, formData);
        
        // Emitir evento con los influencers agregados
        this.$emit('influencers-added', selectedInfluencerData);
        
        // Mostrar mensaje de éxito
        this.$emit('show-success', 
          `${selectedInfluencerData.length} influencer${selectedInfluencerData.length !== 1 ? 's' : ''} agregado${selectedInfluencerData.length !== 1 ? 's' : ''} correctamente`
        );
        
        // Limpiar selección
        this.selectedInfluencers = [];
        
        // Cerrar el sidebar
        this.close();
      } catch (error) {
        console.error('Error al agregar influencers:', error);
        this.$emit('show-error', 'Ocurrió un error al agregar los influencers');
      } finally {
        this.loading = false;
      }
    },
    
    formatFollowers(followers) {
      if (!followers) return '0';
      
      if (followers >= 1000000) {
        return `${(followers / 1000000).toFixed(1)}M`;
      } else if (followers >= 1000) {
        return `${(followers / 1000).toFixed(1)}K`;
      }
      return followers.toString();
    },
    
    getNetworkIcon(network) {
      const networkIcons = {
        'instagram': 'InstagramIcon',
        'youtube': 'YoutubeIcon',
        'tiktok': 'VideoIcon', // Feather no tiene ícono específico para TikTok
        'twitter': 'TwitterIcon',
        'twitch': 'TvIcon' // Usamos TvIcon para Twitch
      };
      
      return networkIcons[network] || 'UserIcon';
    },
    
    selectNetwork(network) {
      this.filters.network = network;
      this.searchInfluencers();
    }
  }
}
</script>

<style scoped>
/* Estilos para el sidebar de agregar influencers */
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');

.font-montserrat, .font-montserrat * {
  font-family: 'Montserrat', sans-serif !important;
}

.add-influencer-sidebar {
  position: fixed;
  top: 0;
  right: -450px;
  width: 450px;
  height: 100vh;
  background-color: #121212;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  transition: right 0.5s ease, opacity 0.4s ease;
  color: white !important;
  opacity: 0;
  font-family: 'Montserrat', sans-serif !important;
}

.add-influencer-sidebar.open {
  right: 0;
  opacity: 1;
  transition: right 0.5s ease, opacity 0.5s ease 0.1s;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1050;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0.5s ease;
  backdrop-filter: blur(3px);
}

.sidebar-overlay.visible {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.5s ease, visibility 0s;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Asegurar que el título siempre sea blanco */
.add-influencer-sidebar .sidebar-header .sidebar-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #FFFFFF !important;
}

/* Mantener la clase original por compatibilidad */
.sidebar-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #FFFFFF !important;
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sidebar-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  color: white !important;
}

.sidebar-content h4 {
  color: white !important;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
}

.sidebar-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.cancel-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Montserrat', sans-serif;
}

.cancel-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.add-button {
  background-color: #1DB954;
  border: none;
  color: white;
  padding: 8px 20px;
  border-radius: 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Montserrat', sans-serif;
}

.add-button:hover:not(:disabled) {
  background-color: #1ed760;
  transform: scale(1.05);
}

.add-button:disabled {
  background-color: #1DB95480;
  cursor: not-allowed;
}

.filter-section, .search-results {
  margin-bottom: 24px;
}

.filter-group {
  margin-bottom: 16px;
}

.filter-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: white !important;
  font-family: 'Montserrat', sans-serif;
}

.filter-select {
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  background-color: #1E1E1E;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.filter-select, .search-input, input {
  font-family: 'Montserrat', sans-serif;
}

.loading-results, .empty-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  text-align: center;
  color: #b3b3b3;
}

.spinner-container {
  margin-bottom: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #1DB954;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-icon {
  color: #b3b3b3;
  margin-bottom: 16px;
}

.influencer-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.influencer-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #1E1E1E;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.influencer-item:hover {
  background-color: #2E2E2E;
}

.influencer-item.selected {
  background-color: rgba(29, 185, 84, 0.1);
  border: 1px solid rgba(29, 185, 84, 0.5);
}

.influencer-avatar {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: visible !important;
  margin-right: 16px;
  flex-shrink: 0;
}

.influencer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
}

.network-icon-container {
  position: absolute;
  bottom: -6px;
  right: -6px;
  z-index: 5;
}

.network-icon {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #121212;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.network-icon-svg {
  color: white !important;
  width: 14px !important;
  height: 14px !important;
}

.network-instagram {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
}

.network-youtube {
  background-color: #FF0000;
}

.network-tiktok {
  background-color: #000000;
}

.network-twitter {
  background-color: #1DA1F2;
}

.network-twitch {
  background-color: #6441A5;
}

.influencer-info {
  flex: 1;
}

.influencer-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
  color: white !important;
  font-family: 'Montserrat', sans-serif;
}

.influencer-stats {
  font-size: 14px;
  color: #b3b3b3;
  margin: 0;
  font-family: 'Montserrat', sans-serif;
}

.selection-checkbox {
  margin-left: 16px;
}

.text-primary {
  color: #1DB954;
}

.text-muted {
  color: #b3b3b3;
}

/* Estilos para el buscador dentro del sidebar */
.search-container {
  margin-bottom: 24px;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #b3b3b3;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border-radius: 4px;
  background-color: #1E1E1E;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 400;
}

.search-input:focus {
  outline: none;
  border-color: #1DB954;
}

.mb-3 {
  margin-bottom: 16px;
}

.mb-4 {
  margin-bottom: 24px;
}

/* Estilos para los iconos de redes sociales */
.social-networks-filter {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 16px;
}

.network-icon-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  background-color: #1E1E1E;
  border-radius: 8px;
  cursor: pointer;
  width: calc(16.66% - 8px); /* Ajustado para 6 iconos por fila */
  transition: all 0.2s;
  margin-bottom: 8px;
}

.network-icon-btn:hover {
  background-color: #2E2E2E;
}

.network-icon-btn.active {
  background-color: rgba(29, 185, 84, 0.2);
  box-shadow: 0 0 0 1px rgba(29, 185, 84, 0.5);
}

.network-name {
  font-size: 12px;
  margin-top: 4px;
  color: white;
  font-family: 'Montserrat', sans-serif;
}

.empty-results p, .loading-results p {
  font-family: 'Montserrat', sans-serif;
}
</style>

<style>
/* Estilos globales */
.add-influencer-sidebar input,
.add-influencer-sidebar button,
.add-influencer-sidebar select,
.add-influencer-sidebar textarea,
.add-influencer-sidebar p,
.add-influencer-sidebar h1,
.add-influencer-sidebar h2,
.add-influencer-sidebar h3,
.add-influencer-sidebar h4,
.add-influencer-sidebar h5,
.add-influencer-sidebar h6,
.add-influencer-sidebar span,
.add-influencer-sidebar div {
  font-family: 'Montserrat', sans-serif !important;
}

/* Asegurar que el título siempre sea blanco */
.add-influencer-sidebar .sidebar-title {
  color: #FFFFFF !important;
  font-family: 'Montserrat', sans-serif !important;
}

.text-white {
  color: white !important;
}
</style> 